import { useEffect, useRef, useState } from "react";
import { getLang } from "./i18n";

// module-level cache so switching between views never re-translates
const cache = new Map(); // source text -> translated (for the current lang)

// Quick heuristic: does this text look Spanish? English-side users only pay
// for LLM calls on texts that actually need translating.
const SPANISH_RE = /[áéíóúñü¿¡]|\b(el|la|los|las|una|es|está|estoy|para|porque|mañana|necesito|necesita|arreglar|fuga|bomba|piscina|hay|pero|con|sin|más|hola|gracias|voy|vendré|revisar|terminado|listo|roto|reparar|hacer|puedo|cuando|donde|también|aquí|todavía|nada|todo|trabajo|unidad)\b/i;

/** Auto-translate dynamic content (ticket titles, details, replies) so
 * everyone reads their own language:
 * - Spanish UI: everything is translated to Spanish.
 * - English UI: Spanish-looking texts are translated to English. */
export function useAutoTranslate(client, texts) {
  const lang = getLang();
  const [, bump] = useState(0);
  const inFlight = useRef(false);
  const candidates = (texts || []).filter((x) => x && x.trim() && !cache.has(x));
  const wanted = lang === "es"
    ? [...new Set(candidates)]
    : [...new Set(candidates.filter((x) => SPANISH_RE.test(x)))];
  const sig = `${lang}:${wanted.slice(0, 150).join("\u0001")}`;

  useEffect(() => {
    if (!wanted.length || inFlight.current) return;
    inFlight.current = true;
    const batch = wanted.slice(0, 150);
    client.post("/translate", { texts: batch, target: lang === "es" ? "es" : "en" })
      .then(({ data }) => {
        (data.translations || []).forEach((tr, i) => cache.set(batch[i], tr));
        bump((n) => n + 1);
      })
      .catch(() => { /* fall back to original text */ })
      .finally(() => { inFlight.current = false; });
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  return (text) => (text ? cache.get(text) || text : text);
}
